import { api } from "~/api";
import type { DateString } from "~/types/common";

import type {
  TCreateMeeting,
  TExpert,
  TFeedbackMeeting,
  TMeeting,
  TMeetingPatientOption,
  TMeetingSlot,
} from "./meetings-types";

const extendedApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMeetingsPatients: builder.query<TMeetingPatientOption[], void>({
      query: () => ({ url: "meetings/patients" }),
    }),
    getMeetingsExperts: builder.query<TExpert[], { expert_id?: string }>({
      query: (params) => ({ url: "meetings", params }),
    }),
    getMeetingFeedback: builder.query<TMeeting | null, void>({
      query: () => ({ url: "meetings/feedback" }),
      providesTags: ["MeetingFeedback"],
    }),
    postMeetingFeedback: builder.mutation<void, TFeedbackMeeting>({
      query: (data) => ({ url: "meetings/feedback", method: "POST", body: data }),
      invalidatesTags: ["MeetingFeedback"],
    }),
    delayMeetingFeedback: builder.mutation<void, { meeting_id: number }>({
      query: (data) => ({ url: "meetings/feedback", method: "PATCH", body: data }),
    }),
    getDoctorMeetings: builder.query<TMeeting[], void>({
      query: () => ({ url: "meetings/doctor" }),
      providesTags: ["Meeting"],
    }),
    getMeeting: builder.query<TMeeting, { meetingId: string }>({
      query: (params) => ({ url: `meetings/doctor/${params.meetingId}` }),
      providesTags: ["Meeting"],
    }),
    getMonthSlots: builder.query<TMeetingSlot[], { expert_id: string; date: DateString }>({
      query: ({ expert_id, ...params }) => ({ url: `meetings/${expert_id}/slots`, params }),
    }),
    postMeeting: builder.mutation<TMeeting, TCreateMeeting>({
      query: (data) => ({ url: "meetings", method: "POST", body: data }),
      invalidatesTags: ["Meeting"],
    }),
    rescheduleMeeting: builder.mutation<
      void,
      { meetingId: string; reschedule_reason: string | null | undefined; start_time: DateString }
    >({
      query: ({ meetingId, ...data }) => ({
        url: `meetings/doctor/${meetingId}/reschedule`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Meeting"],
    }),
    cancelMeeting: builder.mutation<
      void,
      { meetingId: string; cancel_reason: string | null | undefined }
    >({
      query: ({ meetingId, ...data }) => ({
        url: `meetings/doctor/${meetingId}/cancel`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Meeting"],
    }),
  }),
});

export const {
  useGetMeetingsPatientsQuery,
  useGetMeetingsExpertsQuery,
  useGetMeetingFeedbackQuery,
  usePostMeetingFeedbackMutation,
  useDelayMeetingFeedbackMutation,
  useGetDoctorMeetingsQuery,
  useLazyGetDoctorMeetingsQuery,
  useGetMeetingQuery,
  useGetMonthSlotsQuery,
  usePostMeetingMutation,
  useRescheduleMeetingMutation,
  useCancelMeetingMutation,
} = extendedApi;
